import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, Store, Flag, CreditCard, LogOut, Shield, RefreshCw } from 'lucide-react';
import { Button } from '@lecartte/shared-ui/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import logoSrc from '@/assets/logo.svg';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
  { to: '/usuarios', label: 'Usuários', icon: <Users size={18} /> },
  { to: '/restaurantes', label: 'Restaurantes', icon: <Store size={18} /> },
  { to: '/funcionalidades', label: 'Funcionalidades', icon: <Flag size={18} /> },
  { to: '/planos', label: 'Planos', icon: <CreditCard size={18} /> },
  { to: '/reprocessar', label: 'Reprocessar', icon: <RefreshCw size={18} /> },
];

function NavItemRow({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/' || item.to === '/funcionalidades' || item.to === '/planos'}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? 'bg-primary text-white'
            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
        }`
      }
    >
      {item.icon}
      <span>{item.label}</span>
    </NavLink>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();

  return (
    <aside className="flex flex-col w-56 shrink-0 bg-slate-900 border-r border-slate-800 h-screen sticky top-0">
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 py-5 border-b border-slate-800">
        <img src={logoSrc} alt="LeCartte" className="h-9 w-20 rounded-xl object-cover" />
        <div className="w-px h-4 bg-slate-700" />
        <Shield size={16} className="text-primary" />
        <span className="font-display font-bold text-white text-sm">Console</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1">
        {navItems.map((item) => (
          <NavItemRow key={item.to} item={item} />
        ))}
      </nav>

      {/* User footer */}
      <div className="px-3 py-3 border-t border-slate-800">
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="size-7 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white">
            {user?.name?.[0]?.toUpperCase() ?? 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            title="Sair"
            className="text-slate-400 hover:text-white hover:bg-transparent px-1.5"
          >
            <LogOut size={14} />
          </Button>
        </div>
      </div>
    </aside>
  );
}
